import { Injectable } from "@angular/core";
import GeoJSON from "ol/format/GeoJSON";
import { parse, stringify } from "wellknown";
import { booleanPointInPolygon } from "@turf/boolean-point-in-polygon";
import { intersect } from "@turf/intersect";
import { buffer } from "@turf/buffer";
import { createEmpty, extend } from "ol/extent";
import { fromExtent } from "ol/geom/Polygon";
import { Observable, Observer, from, of } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import * as FormatUtils from "@vision/core-map-api/src/utils/FormatUtils";
import { booleanIntersects } from "@turf/boolean-intersects";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import {
  GeometryCollection,
  LineString,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  BBox,
  Feature,
  Geometry,
  Point,
  Polygon,
  FeatureCollection,
} from "geojson";
import { feature, point, featureCollection } from "@turf/helpers";
import { bboxPolygon } from "@turf/bbox-polygon";
import { bbox } from "@turf/bbox";
import { toMercator, toWgs84 } from "@turf/projection";
import { booleanContains } from "@turf/boolean-contains";
import { distance } from "@turf/distance";
import { centroid } from "@turf/centroid";
import { flatten } from "@turf/flatten";
import { truncate } from "@turf/truncate";
import { voronoi } from "@turf/voronoi";
// eslint-disable-next-line import/no-extraneous-dependencies
import polygonClipping from "polygon-clipping";

/** A single polygon geometry with an optional imagery collection override. */
export interface AreaItem {
  wkt: string;
  collection?: string;
}
// import { hint } from "@mapbox/geojsonhint";
// todo poner de neuvo esto o reemplazarlo pro otra libreria
export interface NormalizationResult {
  data: FeatureCollection<any> | null;
  status: "ok" | "warning" | "error";
  errors: { feature: Feature<any> | null; message: string }[];
}

@Injectable({
  providedIn: "root",
})
export class GeoService {
  /** WGS84 semi-major axis in meters, used as the sphere radius for geodesic area calculations. */
  private static readonly EARTH_RADIUS_METERS = 6378137;

  /**
   * Minimum edge-tile overlap, in metres, for a tile to be considered part of an area.
   * An intersection narrower than this in either direction can't hold meaningful imagery
   * for a detection request regardless of tile size — used by both {@link tileArea} (to
   * discard sliver intersections) and {@link countAreaTiles} (to keep the cheap estimate
   * consistent with the tiles {@link tileArea} would actually produce).
   */
  private static readonly MIN_TILE_DIMENSION_M = 1;

  /**
   * Cap on how many bbox-range tiles {@link countAreaTiles} will individually intersect
   * against the real geometry for an exact count. Above this, the bbox range count is
   * returned as-is — always ≥ the real tile count, so it's still safe for an "area too
   * large" check — instead of running a turf intersection per tile, which can freeze the
   * browser for a very large or complex drawn area.
   */
  private static readonly MAX_ACCURATE_TILE_COUNT = 4096;

  /**
   * Approximate width/height, in metres, of a GeoJSON geometry's bbox at its own latitude.
   * Used to reject sliver tile intersections that are geometrically real but too thin to
   * hold meaningful imagery — see {@link tileArea} and {@link countAreaTiles}.
   */
  private static bboxSizeMeters(g: any): { widthM: number; heightM: number } {
    const [minX, minY, maxX, maxY] = bbox(g) as [number, number, number, number];
    const midLatRad = ((minY + maxY) / 2) * (Math.PI / 180);
    return {
      widthM: Math.max(0, maxX - minX) * 111000 * Math.cos(midLatRad),
      heightM: Math.max(0, maxY - minY) * 111000,
    };
  }

  // Coordinate format patterns organized for reusability
  private static readonly COORDINATE_PATTERNS = [
    {
      name: "simple",
      pattern: /^\(?(-?\d+(\.\d+)?)[,\s]\s*(-?\d+(\.\d+)?)\)?$/,
      latIndex: 1,
      lngIndex: 3,
      description: "Simple coordinates with comma or space delimiter",
    },
    {
      name: "wkt",
      pattern: /^POINT\s*\(\s*(-?\d+(\.\d+)?)[,\s]\s*(-?\d+(\.\d+)?)\s*\)$/i,
      latIndex: 1,
      lngIndex: 3,
      description: "WKT Point format",
    },
    {
      name: "geojson",
      pattern:
        /^\s*\{\s*"type"\s*:\s*"Point"\s*,\s*"coordinates"\s*:\s*\[\s*(-?\d+(\.\d+)?)\s*,\s*(-?\d+(\.\d+)?)\s*\]\s*\}\s*$/i,
      latIndex: 1,
      lngIndex: 3,
      description: "GeoJSON Point format",
    },
  ];

  private static waitForGoogleMaps(): Promise<void> {
    const maxWaitTime = 5000;
    const checkInterval = 100;
    let elapsed = 0;

    return new Promise((resolve, reject) => {
      const tryCheck = () => {
        if ((window as any).google && (window as any).google.maps) {
          resolve();
        } else if (elapsed >= maxWaitTime) {
          reject(new Error("Google Maps API not loaded after 5 seconds"));
        } else {
          elapsed += checkInterval;
          setTimeout(tryCheck, checkInterval);
        }
      };
      tryCheck();
    });
  }

  static isCoordinates(location: string): boolean {
    // Use pattern array to validate coordinates - stops on first match
    for (const pattern of GeoService.COORDINATE_PATTERNS) {
      if (pattern.pattern.test(location)) {
        return true;
      }
    }
    return false;
  }

  static parseCoordinates(location: string): number[] | null {
    if (!GeoService.isCoordinates(location)) {
      return null;
    }

    // Use pattern array to parse coordinates - stops on first match
    for (const pattern of GeoService.COORDINATE_PATTERNS) {
      const match = location.match(pattern.pattern);
      if (match) {
        return [parseFloat(match[pattern.latIndex]), parseFloat(match[pattern.lngIndex])];
      }
    }

    return null;
  }

  static reverseGeocode(coordinates: number[]): Observable<google.maps.GeocoderResult[]> {
    return from(GeoService.waitForGoogleMaps()).pipe(
      catchError((err) => {
        console.warn("Google Maps not loaded for reverseGeocode:", err);
        return of([]);
      }),
      switchMap(() => {
        return new Observable((observer: Observer<google.maps.GeocoderResult[]>) => {
          const geocoder = new google.maps.Geocoder();
          const request = {
            location: { lat: coordinates[0], lng: coordinates[1] },
          };
          geocoder.geocode(request, (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
            if (status === google.maps.GeocoderStatus.OK) {
              observer.next(results);
              observer.complete();
            } else {
              observer.error(status);
            }
          });
        });
      }),
    );
  }

  static geocode(options: any): Observable<google.maps.GeocoderResult[]> {
    return from(GeoService.waitForGoogleMaps()).pipe(
      catchError((err) => {
        console.warn("Google Maps not loaded for geocode:", err);
        return of([]);
      }),
      switchMap(() => {
        return new Observable((observer: Observer<google.maps.GeocoderResult[]>) => {
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode(options, (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
            if (status === google.maps.GeocoderStatus.OK) {
              observer.next(results);
              observer.complete();
            } else {
              observer.error(status);
            }
          });
        });
      }),
    );
  }

  static geocoding(address: string): Observable<google.maps.GeocoderResult[]> {
    return from(GeoService.waitForGoogleMaps()).pipe(
      catchError((err) => {
        console.warn("Google Maps not loaded for geocoding:", err);
        return of([]);
      }),
      switchMap(() => {
        return new Observable((observer: Observer<google.maps.GeocoderResult[]>) => {
          const geocoder = new google.maps.Geocoder();
          const request = { address };
          geocoder.geocode(request, (results: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
            if (status === google.maps.GeocoderStatus.OK) {
              observer.next(results);
              observer.complete();
            } else {
              observer.error(status);
            }
          });
        });
      }),
    );
  }

  static geometryToGeoJSON(olGeometry, options = {}) {
    return new GeoJSON().writeGeometryObject(olGeometry, options);
  }

  static geometryToWKT(geometry, decimalPrecision = null): string {
    const geoJSON = GeoService.geometryToGeoJSON(geometry, {}) as Polygon;

    if (decimalPrecision && geoJSON && geoJSON.coordinates) {
      // Helper to round numbers
      const round = (n: number) => parseFloat(n.toFixed(decimalPrecision));

      // Recursively apply rounding to coordinates
      const roundCoords = (coords: any): any => {
        if (typeof coords[0] === "number") {
          // Point: [x, y]
          return coords.map(round);
        }
        // Array of coordinates (LineString, Polygon, etc.)
        return coords.map(roundCoords);
      };

      geoJSON.coordinates = roundCoords(geoJSON.coordinates);
    }

    return GeoService.geoJSONFeatureToWKT(geoJSON);
  }

  static geometryTo2D(geometry) {
    if (geometry.getType() === "Polygon") {
      // only transform first linear ring
      const linearRings = geometry.getLinearRings().map((ring) => this.coordinatesTo2D(ring.getCoordinates()));
      geometry.setCoordinates(linearRings);
    } else if (geometry.getType() === "LineString") {
      geometry.setCoordinates(this.coordinatesTo2D(geometry.getCoordinates()));
    } else if (geometry.getType() === "Point") {
      geometry.setCoordinates(this.to2D(geometry.getCoordinates()));
    } else if (geometry.getType() === "GeometryCollection") {
      const geometries = [];
      geometry.getGeometries().forEach((geo) => {
        geometries.push(this.geometryTo2D(geo));
      });
      geometry.setGeometries(geometries);
    } else {
      throw new Error(`Dont support transform to 3d for geometry of type ${typeof geometry}`);
    }

    return geometry;
  }

  static coordinatesTo2D(coordinates) {
    return coordinates.map((coord) => this.to2D(coord));
  }

  static to2D(coordinate) {
    if (coordinate.length === 3) {
      coordinate.pop();
    }
    return coordinate;
  }

  static bboxFromPoints(extent: number[]): any {
    return bboxPolygon(extent as BBox);
  }

  static bboxToWkt(extent: number[]): string {
    return stringify(GeoService.bboxFromPoints(extent));
  }

  static polygonToWkt(polygon: Polygon): string {
    return stringify(polygon);
  }

  static pointFromArray(array: number[]): any {
    return point(array);
  }

  static toMercator(geojson: any, mutate: boolean = true): any {
    return geojson ? toMercator(geojson, { mutate }) : null;
  }

  static toWgs84(geojson: any, mutate = false): any {
    return toWgs84(geojson, { mutate });
  }

  static pointToWkt(array: number[]): string {
    return stringify(point(array));
  }

  static wktToPoint(wkt: string): Point {
    return parse(wkt) as Point;
  }

  static wktPolygonToFeature(wkt: string): Feature<Polygon> {
    const geoJSON = GeoService.wktToGeoJSONPolygon(wkt);
    if (geoJSON) {
      return feature(GeoService.wktToGeoJSONPolygon(wkt));
    }
  }

  static geoJSONToFeature(geoJSON) {
    try {
      const geoJSONobject = JSON.parse(geoJSON);
      return feature(geoJSONobject);
    } catch (e) {
      return null;
    }
  }

  static wktMultiPolygonToFeature(wkt: string): Feature<Polygon> {
    return feature(GeoService.wktToGeoJSONMultiPolygon(wkt));
  }

  static wktPointToFeature(wkt: string): Feature<Point> {
    return feature(GeoService.wktToGeoJSONPoint(wkt));
  }

  static wktMultiPointToFeature(wkt: string): Feature<MultiPoint> {
    return feature(GeoService.wktToGeoJSONMultiPoint(wkt));
  }

  static wktLineStringToFeature(wkt: string): Feature<LineString> {
    return feature(GeoService.wktToGeoJSONLineString(wkt));
  }

  static wktMultiLineStringToFeature(wkt: string): Feature<MultiLineString> {
    return feature(GeoService.wktToGeoJSONMultiLineString(wkt));
  }

  static wktGeometryCollectionToFeature(wkt: string): Feature<GeometryCollection> {
    return feature(GeoService.wktToGeoJSONGeometryCollection(wkt));
  }

  static wktPointToCoordinates(wkt: string): number[] {
    return GeoService.wktToGeoJSONPoint(wkt, false).coordinates;
  }

  static wktToGeoJSONPolygon(wkt: string, transformToMercator: boolean = true): any {
    return transformToMercator ? GeoService.toMercator(parse(wkt) as Polygon) : (parse(wkt) as Polygon);
  }

  static wktToGeoJSONMultiPolygon(wkt: string, transformToMercator: boolean = true): any {
    return transformToMercator ? GeoService.toMercator(parse(wkt) as MultiPolygon) : (parse(wkt) as Polygon);
  }

  static wktPolygonToBbox(wkt: string, transformToMercator: boolean = true): any {
    return bbox(GeoService.wktToGeoJSONPolygon(wkt, transformToMercator), { recompute: true });
  }

  static bbox(polygon: Geometry, toPolygon: boolean = false): any {
    return toPolygon ? bboxPolygon(bbox(polygon, { recompute: true })) : bbox(polygon, { recompute: true });
  }

  static featureCollectionBbox(fc: FeatureCollection): any {
    return this.bboxToWkt(bbox(fc, { recompute: true }));
  }

  static createBufferAroundPoint(pointCenter: any, radius: any, options: any = { units: "meters" }): any {
    return buffer(pointCenter, radius, options);
  }

  /**
   * Expands a WKT geometry by `meters` using Turf's geodesic buffer.
   * Returns the buffered geometry as a WKT string, or the original WKT
   * if parsing/buffering fails. Keeps the input/output projection (WGS84).
   *
   * @param wkt   WKT geometry (POLYGON, MULTIPOLYGON, POINT, etc.)
   * @param meters Buffer distance in metres
   */
  static bufferWkt(wkt: string, meters: number): string {
    if (!wkt) return wkt;
    try {
      const fc = GeoService.wktToFeatureCollection(wkt, false);
      const feat = fc?.features?.[0];
      if (!feat) return wkt;
      const buffered = buffer(feat, meters, { units: "meters" });
      if (!buffered) return wkt;
      return (GeoService.geoJSONFeatureToWKT(buffered.geometry) as string) ?? wkt;
    } catch (e) {
      console.warn("GeoService.bufferWkt failed, returning original WKT", e);
      return wkt;
    }
  }

  static wktToGeoJSONPoint(wkt: string, transformToMercator: boolean = true): any {
    return transformToMercator ? GeoService.toMercator(parse(wkt)) : (parse(wkt) as Point);
  }

  static wktToGeoJSONMultiPoint(wkt: string, transformToMercator: boolean = true): any {
    return transformToMercator ? GeoService.toMercator(parse(wkt)) : (parse(wkt) as MultiPoint);
  }

  static wktToGeoJSONLineString(wkt: string, transformToMercator: boolean = true): any {
    return transformToMercator ? GeoService.toMercator(parse(wkt)) : (parse(wkt) as LineString);
  }

  static wktToGeoJSONMultiLineString(wkt: string, transformToMercator: boolean = true): any {
    return transformToMercator ? GeoService.toMercator(parse(wkt)) : (parse(wkt) as MultiLineString);
  }

  static wktToGeoJSONGeometryCollection(wkt: string, transformToMercator: boolean = true): any {
    return transformToMercator ? GeoService.toMercator(parse(wkt)) : (parse(wkt) as GeometryCollection);
  }

  static wktToFeature(wkt, transformToMercator: boolean = true) {
    return transformToMercator ? GeoService.toMercator(parse(wkt)) : parse(wkt);
  }

  /**
   * Converts a WKT string (single or multiple geometries) to a GeoJSON FeatureCollection.
   * Only supports standard 2D OGC types: POINT, LINESTRING, POLYGON, MULTIPOINT, MULTILINESTRING, MULTIPOLYGON, GEOMETRYCOLLECTION.
   * Ignores Z/M/ZM/EMPTY/TIN/POLYHEDRALSURFACE and other non-2D types.
   * Accepts multiple geometries separated by newlines or spaces.
   * @param wkt string containing one or more WKT geometries
   * @param transformToMercator whether to transform to Mercator projection
   * @returns FeatureCollection<any>
   */
  static wktToFeatureCollection(wkt: string, transformToMercator: boolean = true): FeatureCollection<any> {
    if (!wkt || typeof wkt !== "string") return featureCollection([]);

    // Only match standard 2D OGC types, ignore Z/M/EMPTY/etc.
    const geometryType = "(POINT|LINESTRING|POLYGON|MULTIPOINT|MULTILINESTRING|MULTIPOLYGON|GEOMETRYCOLLECTION)";
    const unwantedTypes = /\b(Z|M|ZM|EMPTY|TIN|POLYHEDRALSURFACE)\b/i;

    // Regex to find all geometry blocks (handles nested parentheses)
    const regex = new RegExp(`${geometryType}\\s*\\(([^()]|\\([^()]*\\))*\\)`, "gim");
    const matches = [];
    let match;
    while ((match = regex.exec(wkt)) !== null) {
      const geomStr = match[0];
      if (!unwantedTypes.test(geomStr)) {
        matches.push(geomStr);
      }
    }

    const features: Feature<any>[] = [];
    matches.forEach((singleWkt) => {
      try {
        const geo = parse(singleWkt);
        const featureObj = transformToMercator ? GeoService.toMercator(geo) : geo;
        features.push(feature(featureObj));
      } catch (e) {
        console.warn("Invalid WKT geometry skipped:", singleWkt);
      }
    });

    // If nothing matched, try to parse the whole string as a single geometry
    if (features.length === 0) {
      try {
        const geo = parse(wkt);
        const featureObj = transformToMercator ? GeoService.toMercator(geo) : geo;
        features.push(feature(featureObj));
      } catch (e) {
        console.warn("Invalid WKT input:", wkt);
        return featureCollection([]);
      }
    }

    return featureCollection(features);
  }

  static pointToGeoJSONWGS84(array: number[], transformToWgs84: boolean = true): any {
    return transformToWgs84 ? toWgs84(point(array)) : point(array);
  }

  static pointToMercator(array: number[], mercator: boolean = true): any {
    return mercator ? toMercator(point(array)) : point(array);
  }

  static geoJSONFeatureToWKT(geojson: any): any {
    return stringify(geojson);
  }

  // ── Multi-geometry splitting ───────────────────────────────────────────────

  /**
   * Splits a `MULTIPOLYGON(...)` WKT into an array of individual `POLYGON(...)`
   * WKTs by walking parentheses depth. Each top-level `((ring)[,(ring)...])` block
   * becomes one `POLYGON(...)`. Returns `[wkt]` unchanged for any other type.
   */
  static splitMultiPolygon(wkt: string): string[] {
    // Advance past "MULTIPOLYGON" keyword to the first opening paren
    let i = wkt.search(/MULTIPOLYGON\s*\(/i);
    if (i === -1) return [wkt];
    while (i < wkt.length && wkt[i] !== "(") i += 1;
    i += 1; // skip the outer MULTIPOLYGON( paren

    const polygons: string[] = [];
    let depth = 0;
    let blockStart = -1;

    for (; i < wkt.length; i += 1) {
      const c = wkt[i];
      if (c === "(") {
        if (depth === 0) blockStart = i;
        depth += 1;
      } else if (c === ")") {
        depth -= 1;
        if (depth === 0 && blockStart >= 0) {
          polygons.push(`POLYGON${wkt.substring(blockStart, i + 1)}`);
          blockStart = -1;
        }
      }
    }

    return polygons.length > 0 ? polygons : [wkt];
  }

  /**
   * Splits a `MULTIPOINT(...)` WKT into an array of individual `POINT(x y)` WKTs.
   * Handles both the parenthesised form `MULTIPOINT((x y),(x y))` and the flat
   * form `MULTIPOINT(x y, x y)`. Returns `[wkt]` unchanged for any other type.
   */
  static splitMultiPoint(wkt: string): string[] {
    // Strip the MULTIPOINT keyword and outer wrapping parens
    const inner = wkt
      .replace(/^\s*MULTIPOINT\s*\(\s*/i, "")
      .replace(/\s*\)\s*$/, "")
      .trim();

    const points: string[] = [];

    if (inner.startsWith("(")) {
      // Parenthesised form: (x y),(x y)
      const re = /\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(inner)) !== null) {
        points.push(`POINT(${m[1]} ${m[2]})`);
      }
    } else {
      // Flat form: x y, x y
      const re = /(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)/g;
      let m: RegExpExecArray | null;
      while ((m = re.exec(inner)) !== null) {
        points.push(`POINT(${m[1]} ${m[2]})`);
      }
    }

    return points.length > 0 ? points : [wkt];
  }

  /**
   * Applies a metre buffer to each area item and removes overlap by partitioning the
   * plane into a Voronoi diagram built from each item's site (see below), then clipping
   * every item's buffered polygon to its own Voronoi cell. Voronoi cells never overlap,
   * so this guarantees disjoint output areas in a single pass — no priority ordering,
   * no growing-accumulated-geometry cost: each item's clip only depends on its own
   * pre-computed cell, never on other items or on its position in the input array.
   *
   * The Voronoi site for each item is its centroid: for a `POINT` wkt that's the point
   * itself, for a polygon it's `@turf/centroid`. Sites that land on the exact same
   * coordinate are nudged by a tiny deterministic epsilon before the Voronoi call —
   * `d3-voronoi` (used internally by `@turf/voronoi`) produces degenerate/undefined
   * cells for coincident sites otherwise.
   *
   * Each item retains its `collection`. Items whose buffered area doesn't intersect
   * its own Voronoi cell (fully consumed by neighbouring sites) are dropped from the
   * result.
   *
   * Only call when `bufferM > 0`. Without buffer the original geometries are assumed
   * not to overlap and no clipping is needed.
   *
   * Runs in two passes: first buffering every item and collecting its site, then —
   * once the single O(n log n) Voronoi diagram has been computed from all sites —
   * clipping every item to its cell.
   */
  static priorityClipAreas(items: AreaItem[], bufferM: number): Observable<AreaItem[]> {
    if (!items.length) return of(items);

    const sites: ([number, number] | null)[] = new Array(items.length).fill(null);
    const bufferedGeoms: (Polygon | MultiPolygon | null)[] = new Array(items.length).fill(null);
    const result: AreaItem[] = [];

    const bufferItem = (item: AreaItem, index: number): void => {
      try {
        const rawGeom = parse(item.wkt) as Polygon | Point;
        if (!rawGeom) return;

        sites[index] =
          rawGeom.type === "Point"
            ? (rawGeom.coordinates as [number, number])
            : (centroid(rawGeom).geometry.coordinates as [number, number]);

        // Buffer then truncate to 6 decimal places — removes near-degenerate
        // edges that the Voronoi/intersect step cannot handle.
        bufferedGeoms[index] = (
          truncate(buffer(feature(rawGeom), bufferM, { units: "meters", steps: 4 }), {
            precision: 6,
            coordinates: 2,
          }) as any
        ).geometry as Polygon | MultiPolygon;
      } catch (err) {
        console.error("GeoService.priorityClipAreas: failed to buffer item, keeping original:", err);
      }
    };

    const clipItem = (item: AreaItem, index: number, cell: Polygon | null): void => {
      const bufferedGeom = bufferedGeoms[index];
      if (!bufferedGeom) {
        result.push(item);
        return;
      }
      if (!cell) {
        // No cell available (Voronoi failed, or this item had no valid site) — keep the full buffer.
        result.push({ wkt: stringify(bufferedGeom) ?? item.wkt, collection: item.collection });
        return;
      }
      try {
        const clipped = GeoService.getIntersection(bufferedGeom, cell);
        if (clipped) result.push({ wkt: stringify(clipped.geometry) ?? "", collection: item.collection });
      } catch (err) {
        console.error("GeoService.priorityClipAreas: failed to clip item to its Voronoi cell:", err);
        result.push({ wkt: stringify(bufferedGeom) ?? item.wkt, collection: item.collection });
      }
    };

    items.forEach((item, index) => bufferItem(item, index));
    const cells = GeoService.buildVoronoiCells(sites, bufferedGeoms, bufferM);
    items.forEach((item, index) => clipItem(item, index, cells[index]));

    return of(result.filter((a) => a.wkt));
  }

  /**
   * Builds one Voronoi cell per site (same order as `sites`/`bufferedGeoms`, `null`
   * where the site itself was invalid). The clipping bbox passed to `@turf/voronoi`
   * is grown to fully contain every buffered geometry, so a cell is only ever cut by
   * a neighbouring site, never by the diagram's outer extent.
   */
  private static buildVoronoiCells(
    sites: readonly ([number, number] | null)[],
    bufferedGeoms: readonly (Polygon | MultiPolygon | null)[],
    bufferM: number,
  ): (Polygon | null)[] {
    const cells: (Polygon | null)[] = sites.map(() => null);
    try {
      // Invalid sites (buffering failed for that item) are left out of the Voronoi
      // input entirely, rather than filled with a placeholder coordinate — a
      // placeholder would still participate in the diagram and could distort the
      // cells of real neighbouring sites.
      const validIndices: number[] = [];
      const seen = new Set<string>();
      const jitteredSites: [number, number][] = [];
      for (let i = 0; i < sites.length; i++) {
        const site = sites[i];
        if (!site) continue;
        validIndices.push(i);
        const [x, y] = site;
        const key = `${x},${y}`;
        // Nudge exact-duplicate coordinates — d3-voronoi (used internally by
        // @turf/voronoi) produces degenerate/undefined cells for coincident sites.
        jitteredSites.push(seen.has(key) ? [x + 1e-9 * (i + 1), y + 1e-9 * (i + 1)] : [x, y]);
        seen.add(key);
      }
      if (!jitteredSites.length) return cells;

      const validBboxes = bufferedGeoms.filter((g): g is Polygon | MultiPolygon => !!g).map((g) => bbox(g));
      if (!validBboxes.length) return cells;
      const margin = Math.max(bufferM / 111000, 0.001);
      const voronoiBbox: BBox = [
        Math.min(...validBboxes.map((b) => b[0])) - margin,
        Math.min(...validBboxes.map((b) => b[1])) - margin,
        Math.max(...validBboxes.map((b) => b[2])) + margin,
        Math.max(...validBboxes.map((b) => b[3])) + margin,
      ];

      const diagram = voronoi(featureCollection(jitteredSites.map((s) => point(s))), { bbox: voronoiBbox });
      validIndices.forEach((originalIndex, i) => {
        cells[originalIndex] = (diagram.features[i]?.geometry as Polygon) ?? null;
      });
      return cells;
    } catch (err) {
      console.error("GeoService.priorityClipAreas: Voronoi computation failed, skipping overlap removal:", err);
      return cells;
    }
  }

  /**
   * Unions an arbitrary number of 2D polygon rings using `polygon-clipping`.
   * Each input polygon is a ring: `[[x, y], [x, y], ...]` (closed or open).
   * Returns the union as an array of polygons in `polygon-clipping` format:
   * `Array<Array<Array<[x, y]>>>` where each polygon is `[exterior, ...holes]`.
   * Returns an empty array if the input is empty or the union fails.
   */
  static unionPolygons(rings: Array<Array<[number, number]>>): [number, number][][][] {
    if (!rings.length) return [];
    const polys: [number, number][][][] = rings
      .filter((r) => r.length >= 3)
      .map((r) => {
        const first = r[0];
        const last = r[r.length - 1];
        const closed =
          first[0] === last[0] && first[1] === last[1] ? r : [...r, [first[0], first[1]] as [number, number]];
        return [closed];
      });
    if (!polys.length) return [];
    try {
      return polygonClipping.union(polys[0], ...polys.slice(1)) as [number, number][][][];
    } catch (err) {
      console.error("GeoService.unionPolygons: union failed", err);
      return [];
    }
  }

  /**
   * Intersects two polygon sets in `polygon-clipping` format (as returned by
   * {@link unionPolygons}: `Array<Polygon>` where each `Polygon` is `[exterior, ...holes]`).
   * Returns an empty array when either input is empty, they don't overlap, or the
   * intersection fails.
   */
  static intersectPolygonSets(a: [number, number][][][], b: [number, number][][][]): [number, number][][][] {
    if (!a.length || !b.length) return [];
    try {
      return polygonClipping.intersection(a, b) as [number, number][][][];
    } catch (err) {
      console.error("GeoService.intersectPolygonSets: intersection failed", err);
      return [];
    }
  }

  /**
   * Planar area (shoelace formula) of a polygon set in `polygon-clipping` format, subtracting
   * each polygon's holes from its exterior ring. Coordinates are treated as flat/planar —
   * fine for comparing small local footprints (e.g. detection masks) against each other, since
   * the same approximation applies uniformly to both sides of a ratio like IoU.
   */
  static polygonSetArea(polys: [number, number][][][]): number {
    const ringArea = (ring: [number, number][]): number => {
      let sum = 0;
      for (let i = 0; i < ring.length - 1; i++) {
        const [x1, y1] = ring[i];
        const [x2, y2] = ring[i + 1];
        sum += x1 * y2 - x2 * y1;
      }
      return Math.abs(sum) / 2;
    };
    let total = 0;
    for (const [exterior, ...holes] of polys) {
      total += ringArea(exterior) - holes.reduce((s, h) => s + ringArea(h), 0);
    }
    return total;
  }

  /**
   * Geodesic area, in square meters, of a WGS84 GeoJSON geometry.
   *
   * Uses the spherical-excess formula over each polygon ring (holes subtracted), which is the
   * standard approach for planet-scale areas — unlike {@link polygonSetArea}, which is planar
   * and only meaningful for small local footprints. Non-areal geometries (points, lines) return 0;
   * `GeometryCollection` sums the area of its areal members.
   *
   * @param geometry GeoJSON geometry with geographic (EPSG:4326) coordinates
   * @returns Area in square meters, or 0 when the geometry has no area
   */
  static geodesicArea(geometry: Geometry): number {
    if (!geometry) return 0;

    switch (geometry.type) {
      case "Polygon":
        return GeoService.polygonRingsArea((geometry as Polygon).coordinates);
      case "MultiPolygon":
        return (geometry as MultiPolygon).coordinates.reduce(
          (total, rings) => total + GeoService.polygonRingsArea(rings),
          0,
        );
      case "GeometryCollection":
        return (geometry as GeometryCollection).geometries.reduce(
          (total, child) => total + GeoService.geodesicArea(child),
          0,
        );
      default:
        return 0;
    }
  }

  /**
   * Geodesic area, in square meters, of a WKT geometry expressed in geographic (EPSG:4326)
   * coordinates. Returns 0 for unparseable input or geometries without area, so callers can
   * treat "no measurable area" and "not a polygon" uniformly.
   *
   * @param wkt WKT geometry string in geographic coordinates
   * @returns Area in square meters, or 0 when it cannot be measured
   */
  static wktArea(wkt: string): number {
    if (!wkt || typeof wkt !== "string") return 0;
    try {
      const geometry = parse(wkt) as Geometry | null;
      return GeoService.geodesicArea(geometry);
    } catch (error) {
      console.error("GeoService.wktArea: could not parse WKT", error);
      return 0;
    }
  }

  /**
   * Area of a circle of the given radius, in square meters. Used for point + radius geofencing,
   * where the exact circle area is preferable to measuring its polygonal approximation.
   *
   * @param radiusMeters Circle radius in meters
   * @returns Area in square meters, or 0 for a non-positive/invalid radius
   */
  static circleArea(radiusMeters: number): number {
    if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) return 0;
    return Math.PI * radiusMeters * radiusMeters;
  }

  /**
   * Area of a single polygon: its exterior ring minus every hole.
   */
  private static polygonRingsArea(rings: number[][][]): number {
    if (!rings?.length) return 0;
    const [exterior, ...holes] = rings;
    const holesArea = holes.reduce((total, hole) => total + GeoService.geodesicRingArea(hole), 0);
    return Math.max(0, GeoService.geodesicRingArea(exterior) - holesArea);
  }

  /**
   * Absolute geodesic area of a single linear ring, via the spherical-excess sum of its
   * vertices projected onto a sphere of radius {@link EARTH_RADIUS_METERS}.
   */
  private static geodesicRingArea(ring: number[][]): number {
    if (!ring || ring.length <= 2) return 0;

    let total = 0;
    for (let i = 0; i < ring.length; i++) {
      const lower = ring[i];
      const middle = ring[(i + 1) % ring.length];
      const upper = ring[(i + 2) % ring.length];
      total +=
        (GeoService.degreesToRadians(upper[0]) - GeoService.degreesToRadians(lower[0])) *
        Math.sin(GeoService.degreesToRadians(middle[1]));
    }

    return Math.abs((total * GeoService.EARTH_RADIUS_METERS * GeoService.EARTH_RADIUS_METERS) / 2);
  }

  /**
   * Converts degrees to radians.
   */
  private static degreesToRadians(degrees: number): number {
    return (degrees * Math.PI) / 180;
  }

  /**
   * Dispatches to the appropriate splitter based on the leading WKT keyword:
   * - `MULTIPOLYGON` → {@link splitMultiPolygon} (one `POLYGON` per ring group)
   * - `MULTIPOINT`   → {@link splitMultiPoint} (one `POINT` per coordinate)
   * - anything else  → `[wkt]` (already a single geometry — no split needed)
   */
  static splitMultiGeometry(wkt: string): string[] {
    const upper = wkt.trimStart().toUpperCase();
    if (upper.startsWith("MULTIPOLYGON")) return GeoService.splitMultiPolygon(wkt);
    if (upper.startsWith("MULTIPOINT")) return GeoService.splitMultiPoint(wkt);
    return [wkt];
  }

  /**
   * Converts an array of GeoJSON features or geometries to a minimal WKT string.
   * Uses OL projection support to transform coordinates from sourceEpsg to targetEpsg.
   * Normalizes Point+MultiPoint, LineString+MultiLineString, Polygon+MultiPolygon into the
   * corresponding MULTI* type when all share the same base type; falls back to GEOMETRYCOLLECTION
   * for mixed types.
   * @param features Array of GeoJSON Feature or Geometry objects
   * @param sourceEpsg EPSG code of the input coordinates (default: 3857 Web Mercator)
   * @param targetEpsg EPSG code for the output WKT coordinates (default: 4326 WGS84)
   */
  static geoJSONFeaturesToWkt(features: any[], sourceEpsg = 3857, targetEpsg = 4326): string | null {
    const olFormat = new GeoJSON();
    const srcProj = `EPSG:${sourceEpsg}`;
    const dstProj = `EPSG:${targetEpsg}`;

    const geometries = features
      .map((f) => {
        const geom = f.geometry ?? f;
        if (!geom) return null;
        try {
          const olGeom = olFormat.readGeometry(geom, { dataProjection: srcProj, featureProjection: dstProj });
          return olFormat.writeGeometryObject(olGeom, { dataProjection: dstProj, featureProjection: dstProj });
        } catch {
          return null;
        }
      })
      .filter(Boolean) as any[];

    return GeoService.buildMinimalWkt(geometries);
  }

  /**
   * Converts an array of OpenLayers features to a minimal WKT string.
   * Uses OL projection support to transform coordinates from sourceEpsg to targetEpsg.
   * Produces MULTIPOINT / MULTILINESTRING / MULTIPOLYGON when all features share the same
   * primitive geometry type; falls back to GEOMETRYCOLLECTION for mixed types.
   * @param features Array of OpenLayers Feature objects
   * @param sourceEpsg EPSG code of the OL feature coordinates (default: 3857 Web Mercator)
   * @param targetEpsg EPSG code for the output WKT coordinates (default: 4326 WGS84)
   */
  static olFeaturesToWkt(features: any[], sourceEpsg = 3857, targetEpsg = 4326): string | null {
    const srcProj = `EPSG:${sourceEpsg}`;
    const dstProj = `EPSG:${targetEpsg}`;

    const geojsons = features
      .map((f) => {
        const geom = f.getGeometry?.();
        if (!geom) return null;
        try {
          return GeoService.geometryToGeoJSON(geom, { dataProjection: dstProj, featureProjection: srcProj });
        } catch {
          return null;
        }
      })
      .filter(Boolean) as any[];

    return GeoService.buildMinimalWkt(geojsons);
  }

  /**
   * Converts an array of OpenLayers features to a GeoJSON FeatureCollection.
   * Uses OL projection support to transform coordinates from sourceEpsg to targetEpsg.
   * Features that fail to convert are skipped (logged via console.error).
   * @param features Array of OpenLayers Feature objects
   * @param sourceEpsg EPSG code of the OL feature coordinates (default: 3857 Web Mercator)
   * @param targetEpsg EPSG code for the output GeoJSON coordinates (default: 4326 WGS84)
   */
  static olFeaturesToGeoJson(features: any[], sourceEpsg = 3857, targetEpsg = 4326): FeatureCollection<any> {
    const srcProj = `EPSG:${sourceEpsg}`;
    const dstProj = `EPSG:${targetEpsg}`;

    const geoFeatures = features
      .map((f) => {
        const geom = f.getGeometry?.();
        if (!geom) return null;
        try {
          const geometry = GeoService.geometryToGeoJSON(geom, { dataProjection: dstProj, featureProjection: srcProj });
          const { geometry: _olGeometry, ...properties } = f.getProperties?.() ?? {};
          return feature(geometry, properties);
        } catch (error) {
          console.error("GeoService.olFeaturesToGeoJson: failed to convert feature", error);
          return null;
        }
      })
      .filter(Boolean) as Feature<any>[];

    return featureCollection(geoFeatures);
  }

  /**
   * Builds the minimal WKT from an array of GeoJSON geometries.
   * Normalizes Point+MultiPoint, LineString+MultiLineString, Polygon+MultiPolygon into the
   * corresponding MULTI* type; falls back to GEOMETRYCOLLECTION for mixed types.
   */
  private static buildMinimalWkt(geojsons: any[]): string | null {
    if (!geojsons.length) return null;
    if (geojsons.length === 1) return (GeoService.geoJSONFeatureToWKT(geojsons[0]) as string) || null;

    const BASE_TYPE: Record<string, string> = {
      Point: "Point",
      MultiPoint: "Point",
      LineString: "LineString",
      MultiLineString: "LineString",
      Polygon: "Polygon",
      MultiPolygon: "Polygon",
    };
    const MULTI_TYPES: Record<string, string> = {
      Point: "MultiPoint",
      LineString: "MultiLineString",
      Polygon: "MultiPolygon",
    };

    const firstBase = BASE_TYPE[geojsons[0].type];
    const allSameBase = firstBase && geojsons.every((g) => BASE_TYPE[g.type] === firstBase);

    if (allSameBase) {
      const allCoords: any[] = [];
      for (const g of geojsons) {
        if (g.type === firstBase) {
          allCoords.push(g.coordinates);
        } else {
          allCoords.push(...g.coordinates); // flatten Multi* into primitive coords
        }
      }
      return (
        (GeoService.geoJSONFeatureToWKT({ type: MULTI_TYPES[firstBase], coordinates: allCoords }) as string) || null
      );
    }

    const individualWkts = geojsons.map((g) => GeoService.geoJSONFeatureToWKT(g) as string).filter(Boolean);
    return individualWkts.length ? `GEOMETRYCOLLECTION (${individualWkts.join(", ")})` : null;
  }

  static intersectPolygonsWkt(wkt1: string, wkt2: string): boolean {
    return GeoService.polygonsIntersection(GeoService.wktToGeoJSONPolygon(wkt1), GeoService.wktToGeoJSONPolygon(wkt2));
  }

  static containsPoint(wktPolygon: string, wktPoint: string): boolean {
    return GeoService.polygonContainsPoint(
      GeoService.wktToGeoJSONPolygon(wktPolygon),
      GeoService.wktToGeoJSONPoint(wktPoint),
    );
  }

  static polygonsContains(polygon1: Polygon, polygon2: Polygon): boolean {
    return booleanContains(polygon1, polygon2);
  }

  static polygonsIntersection(polygon1: Polygon, polygon2: Polygon): boolean {
    return booleanIntersects(polygon1, polygon2);
  }

  static getIntersection(polygon1: any, polygon2: any): any {
    return intersect(featureCollection([feature(polygon1), feature(polygon2)]));
  }

  static polygonContainsPoint(polygonFeature: Polygon, pointFeature: Point): boolean {
    return booleanPointInPolygon(pointFeature, polygonFeature);
  }

  static isCenterEquals(center1: number[], center2: number[], epsilon: number = 1e-3): boolean {
    return Math.abs(center1[0] - center2[0]) < epsilon && Math.abs(center1[1] - center2[1]) < epsilon;
  }

  static distanceBetweenPoints(p1: Point, p2: Point): number {
    return distance(p1, p2);
  }

  static getCenterOfGeometry(geojson: any): any {
    return centroid(geojson);
  }

  /* Get the bbox from a collection of features in webMercator transform to wkt on lat/lon */
  static getBboxWktFromFeatures(plainGeojson: any, projection = ViewerConstants.PROJECTIONS.WebMercator) {
    const features = FormatUtils.geoJsonToFeatures(plainGeojson);
    let bboxWkt;

    // Check if geojson it´s only one point and set these point as extent
    if (
      plainGeojson.features != null &&
      plainGeojson.features.length === 1 &&
      plainGeojson.features[0].geometry.type === "Point"
    ) {
      bboxWkt = FormatUtils.geometryToWKT(features[0].getGeometry(), {
        featureProjection: projection,
        dataProjection: ViewerConstants.PROJECTIONS.WebMercator,
      });
    } else {
      const extent = createEmpty();

      features.map((feat) => {
        return extend(extent, feat.getGeometry().getExtent());
      });

      bboxWkt = FormatUtils.geometryToWKT(fromExtent(extent), {
        featureProjection: projection,
        dataProjection: ViewerConstants.PROJECTIONS.WebMercator,
      });
    }
    return bboxWkt;
  }

  static tileToLatLng(x, y, z): number[] {
    const long = (x / 2 ** z) * 360 - 180;
    const n = Math.PI - (2 * Math.PI * y) / 2 ** z;
    const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));

    return [lat, long];
  }

  static getGridOfBbox(extent: number[], tileGrid: any, tileGridZoom: number): any[] {
    const bboxMercator1 = GeoService.toMercator([extent[0], extent[1]]);
    const bboxMercator2 = GeoService.toMercator([extent[2], extent[3]]);

    // `getTileRangeForExtentAndZ` (unlike a manual `getTileCoordForCoordAndZ` on each
    // corner) applies OL's edge-intersection policy to the max corner — it resolves
    // to the lower tile instead of floor()'s "next tile" — so a bbox edge landing
    // exactly on a tile boundary doesn't spill into an extra, near-zero-area tile.
    const mercatorExtent = [bboxMercator1[0], bboxMercator1[1], bboxMercator2[0], bboxMercator2[1]];
    const tileRange = tileGrid.getTileRangeForExtentAndZ(mercatorExtent, tileGridZoom);

    const features = [];
    for (let i = tileRange.minX; i <= tileRange.maxX; i++) {
      for (let j = tileRange.minY; j <= tileRange.maxY; j++) {
        const tile = [tileGridZoom, i, j];
        const extentMercator = tileGrid.getTileCoordExtent(tile);
        const extent1 = GeoService.toWgs84([extentMercator[0], extentMercator[1]]);
        const extent2 = GeoService.toWgs84([extentMercator[2], extentMercator[3]]);
        const geoJSONGeometryTile = GeoService.wktToGeoJSONPolygon(
          GeoService.bboxToWkt([extent1[0], extent1[1], extent2[0], extent2[1]]),
          false,
        );
        features.push(geoJSONGeometryTile);
      }
    }
    return features;
  }

  /**
   * Parses each polygon WKT once and merges them into a single `MultiPolygon` geometry
   * (rings are just concatenated, no boolean union — callers only need point-membership,
   * not a dissolved outline). Use this to pre-compute a geometry once from a list of WKTs
   * that will be point-tested many times (e.g. once per detection), instead of re-parsing
   * every WKT on every {@link isPointWithinGeometry} call.
   *
   * POINT WKTs are ignored. Returns `null` if no valid polygon is found.
   *
   * @param wkts Array of polygon WKTs in WGS84
   */
  static combinePolygonWkts(wkts: string[]): MultiPolygon | null {
    const polygons: [number, number][][][] = [];
    for (const wkt of wkts) {
      if (!wkt || wkt.trimStart().toUpperCase().startsWith("POINT")) continue;
      try {
        const geom = parse(wkt) as Polygon | MultiPolygon | null;
        if (!geom) continue;
        if (geom.type === "Polygon") polygons.push(geom.coordinates as [number, number][][]);
        else if (geom.type === "MultiPolygon") polygons.push(...(geom.coordinates as [number, number][][][]));
      } catch {
        // skip invalid wkt
      }
    }
    return polygons.length ? { type: "MultiPolygon", coordinates: polygons } : null;
  }

  /**
   * Returns `true` if the [lon, lat] point falls within `geom`.
   * Returns `true` (fail-open) when `geom` is `null`, or on parse/check errors.
   *
   * @param lonLat [longitude, latitude] coordinate to test
   * @param geom   Polygon/MultiPolygon geometry to check against, or `null` (see {@link combinePolygonWkts})
   */
  static isPointWithinGeometry(lonLat: [number, number], geom: Polygon | MultiPolygon | null): boolean {
    if (!geom) return true;
    try {
      return booleanPointInPolygon(point(lonLat), feature(geom) as any);
    } catch {
      return true; // fail open — treat as within on error
    }
  }

  /**
   * Splits a WKT polygon into tile-sized sub-areas when the area spans more than one tile
   * at the given zoom level. Each tile is clipped to the original polygon so the backend
   * never receives area outside the user's selection. POINT geometries pass through unchanged.
   *
   * Returns a single-element array (the original WKT) when:
   * - The geometry is a POINT
   * - The bbox fits within a single tile at the given zoom (no tiling needed)
   * - Tiling produced ≤ 1 valid intersections (safety fallback)
   *
   * @param wkt      WKT in WGS84 (POLYGON, MULTIPOLYGON, or POINT)
   * @param tileGrid OpenLayers TileGrid from `MapService.getCoreMap().getTileGrid()`
   * @param zoom     OL zoom level for tile size
   */
  static tileArea(wkt: string, tileGrid: any, zoom: number): string[] {
    if (!wkt) return [wkt];

    if (wkt.trimStart().toUpperCase().startsWith("POINT")) return [wkt];

    try {
      const geom = parse(wkt) as Polygon | null;
      if (!geom) return [wkt];

      const extent = bbox(geom) as [number, number, number, number];
      const tilePolygons = GeoService.getGridOfBbox(extent, tileGrid, zoom);

      // Only tile when the area spans more than one tile
      if (tilePolygons.length <= 1) return [wkt];

      // An input WKT edge that's meant to sit exactly on a tile boundary rarely does
      // in floating point — coordinate round-trips (lon/lat<->mercator) or the input
      // WKT simply being formatted with fewer decimals than the true tile edge can
      // leave it a fraction of a millimetre past the boundary. That's enough for the
      // real tile to register as spanning into a neighbour, producing a sliver
      // intersection there. A ratio-of-tile-area cutoff isn't reliable for this: the
      // sliver keeps the tile's full width/height in one axis and collapses only the
      // other, so its area scales with tile size instead of staying uniformly tiny.
      // Instead, reject on an absolute, zoom-independent minimum: an intersection
      // narrower than MIN_TILE_DIMENSION_M in either direction can't hold meaningful
      // imagery for a detection request regardless of tile size.
      const results: string[] = [];
      for (const tilePoly of tilePolygons) {
        const intersection = GeoService.getIntersection(tilePoly, geom);
        if (!intersection) continue;
        const { widthM, heightM } = GeoService.bboxSizeMeters(intersection.geometry);
        if (widthM < GeoService.MIN_TILE_DIMENSION_M || heightM < GeoService.MIN_TILE_DIMENSION_M) continue;
        const intersectionWkt = GeoService.geoJSONFeatureToWKT(intersection.geometry) as string;
        if (intersectionWkt) results.push(intersectionWkt);
      }

      return results.length > 1 ? results : [wkt];
    } catch (e) {
      console.error("GeoService.tileArea: error tiling WKT, returning original:", e);
      return [wkt];
    }
  }

  /**
   * Returns the number of tiles at `zoom` that {@link tileArea} would actually produce for a
   * WKT polygon. Returns 0 for empty/null WKT and 1 for POINT geometries.
   *
   * For a non-convex or diagonal polygon, most of its bbox's tile *range* (min/max tile X/Y)
   * doesn't overlap the polygon at all, or only in a sliver — a plain range count (its
   * width × height in tiles) overcounts in exactly those cases, showing more tiles than are
   * ever downloaded. To stay accurate, each range tile is intersected against the real
   * geometry and kept only if that intersection is at least {@link MIN_TILE_DIMENSION_M} in
   * both directions — the same rule {@link tileArea} uses to decide what to keep.
   *
   * This is O(n) in the number of bbox-range tiles, same order of work as {@link tileArea}
   * itself, so above {@link MAX_ACCURATE_TILE_COUNT} the range count is returned as-is
   * instead of intersecting every tile — see that constant's doc for why that's still safe.
   *
   * @param wkt      WKT in WGS84
   * @param tileGrid OpenLayers TileGrid from `MapService.getCoreMap().getTileGrid()`
   * @param zoom     OL zoom level for tile size
   */
  static countAreaTiles(wkt: string, tileGrid: any, zoom: number): number {
    if (!wkt) return 0;
    if (wkt.trimStart().toUpperCase().startsWith("POINT")) return 1;
    try {
      const geom = parse(wkt);
      if (!geom) return 0;
      const extent = bbox(geom) as [number, number, number, number];

      // Check the raw tile range BEFORE building any tile polygons: getGridOfBbox
      // constructs a GeoJSON polygon (WGS84 conversion + WKT parse) per tile in the range,
      // which is itself enough to freeze the browser for a very large drawn area — the
      // MAX_ACCURATE_TILE_COUNT check has to happen before that loop runs, not after.
      const bboxMercator1 = GeoService.toMercator([extent[0], extent[1]]);
      const bboxMercator2 = GeoService.toMercator([extent[2], extent[3]]);
      const mercatorExtent = [bboxMercator1[0], bboxMercator1[1], bboxMercator2[0], bboxMercator2[1]];
      const tileRange = tileGrid.getTileRangeForExtentAndZ(mercatorExtent, zoom);
      const rawCount = (tileRange.maxX - tileRange.minX + 1) * (tileRange.maxY - tileRange.minY + 1);
      if (rawCount <= 1) return rawCount;
      if (rawCount > GeoService.MAX_ACCURATE_TILE_COUNT) return rawCount;

      const tilePolygons = GeoService.getGridOfBbox(extent, tileGrid, zoom);
      let count = 0;
      for (const tilePoly of tilePolygons) {
        const intersection = GeoService.getIntersection(tilePoly, geom);
        if (!intersection) continue;
        const { widthM, heightM } = GeoService.bboxSizeMeters(intersection.geometry);
        if (widthM < GeoService.MIN_TILE_DIMENSION_M || heightM < GeoService.MIN_TILE_DIMENSION_M) continue;
        count++;
      }

      // Mirrors tileArea's own fallback: ≤ 1 valid intersection means no real tiling, so the
      // request is a single untiled call for the whole area.
      return count > 1 ? count : 1;
    } catch {
      return 0;
    }
  }

  /**
   * Flattens any GeoJSON to a FeatureCollection inspired by geojson-flatten.
   * @param geojson any GeoJSON
   * return FeatureCollection<any> all Multi-Geometries are flattened into single Features
   */
  static flatter(geojson: any) {
    return flatten(geojson);
  }

  static validateGeoJSON(geojson: any): any[] {
    return [];
    // todo descomentar
    // return hint(geojson, {
    //   ignoreRightHandRule: true,
    // });
  }

  /**
   * Normalize any input (Feature, FeatureCollection, or Geometry) into a composed object.
   * - data: FeatureCollection with only valid features, or null if none valid
   * - status: "ok" (all valid) | "warning" (some invalid) | "error" (no valid)
   * - errors: array of { feature, message }
   */
  static normalizeFeatures(input: Feature | FeatureCollection | Geometry | FeatureCollection[]): NormalizationResult {
    if (!input) {
      const msg = "Input is not valid";
      console.warn("Normalization aborted:", msg);
      return {
        data: null,
        status: "error",
        errors: [{ feature: null, message: msg }],
      };
    }

    // Handle array of FeatureCollections (e.g., from zipped shapefiles)
    let featuresToProcess: Feature[] = [];
    if (Array.isArray(input)) {
      input.forEach((fc) => {
        if (fc && fc.type === "FeatureCollection" && Array.isArray(fc.features)) {
          featuresToProcess.push(...fc.features);
        }
      });
    } else if (
      (input as FeatureCollection).type === "FeatureCollection" &&
      Array.isArray((input as FeatureCollection).features)
    ) {
      featuresToProcess = (input as FeatureCollection).features;
    } else if ((input as Feature).type === "Feature") {
      featuresToProcess = [input as Feature];
    } else if ((input as Geometry).type) {
      featuresToProcess = [feature(input as Geometry)];
    }

    const validFeatures: Feature[] = [];
    const errors: { feature: Feature<any> | null; message: string }[] = [];

    const collectFromFeature = (f: Feature<any>) => {
      if (!f) return;
      if (!f.geometry) {
        errors.push({ feature: null, message: "Feature has no geometry" });
        return;
      }
      if (f.geometry.type === "GeometryCollection") {
        const gc = f.geometry as GeometryCollection;
        if (!Array.isArray(gc.geometries)) {
          errors.push({ feature: null, message: "GeometryCollection.geometries is not an array" });
          return;
        }
        gc.geometries.forEach((g) => {
          // A null member would materialize a null-geometry feature that crashes
          // LayerFactory (getGeometry().clone()) when the layer reaches the map. Skip it
          // (warn only — an error entry would fail the whole normalization).
          if (!g) {
            console.warn("normalizeFeatures: skipping null geometry member inside GeometryCollection");
            return;
          }
          const child = feature(g, { ...(f.properties || {}) });
          const geoErrors = GeoService.validateGeoJSON(child);
          if (geoErrors.length) {
            geoErrors.forEach((e) => errors.push({ feature: child, message: e.message }));
          } else {
            validFeatures.push(child);
          }
        });
      } else {
        const geoErrors = GeoService.validateGeoJSON(f);
        if (geoErrors.length) {
          geoErrors.forEach((e) => errors.push({ feature: f, message: e.message }));
        } else {
          validFeatures.push(f);
        }
      }
    };

    featuresToProcess.forEach(collectFromFeature);

    // Compose output FeatureCollection. Preserve top-level `style`, `style_metadata` and
    // `properties` keys from the source FeatureCollection (backend AI contract:
    // `{ layer_id, style, style_metadata }`; custom collection-level properties from imported
    // files) — otherwise they're silently dropped here, before file-reader/attachStyleDefinition
    // ever see them. `style_metadata` preservation per feature doc map-layer-block-ui S6
    // (odd/tasks/map-layer-block-ui.md): without it the backend map_layer style provenance is
    // lost before any consumer (card preview, updateLayerStyleDefinition) can read it.
    const isSourceFeatureCollection = !Array.isArray(input) && (input as FeatureCollection).type === "FeatureCollection";
    const sourceStyle = isSourceFeatureCollection ? (input as any).style : undefined;
    const sourceStyleMetadata = isSourceFeatureCollection ? (input as any).style_metadata : undefined;
    const sourceProperties = isSourceFeatureCollection ? (input as any).properties : undefined;
    const output: FeatureCollection = {
      type: "FeatureCollection",
      features: validFeatures,
      ...(sourceStyle !== undefined ? { style: sourceStyle } : {}),
      ...(sourceStyleMetadata !== undefined ? { style_metadata: sourceStyleMetadata } : {}),
      ...(sourceProperties !== undefined ? { properties: sourceProperties } : {}),
    } as FeatureCollection;
    let status: "ok" | "warning" | "error" = "ok";
    if (validFeatures.length === 0) status = "error";
    else if (errors.length > 0) status = "warning";
    return {
      data: validFeatures.length ? output : null,
      status,
      errors,
    };
  }

  /**
   * Normalize a single feature. If it's valid, return as is.
   * If it's invalid, log error and throw to be caught by caller.
   * @param f Feature<any>
   * @returns Feature<any>[]
   */
  static normalizeFeature(f: Feature<any>): Feature<any>[] {
    if (f.geometry && f.geometry.type === "GeometryCollection") {
      return this.normalizeGeometry(f.geometry);
    }
    const errors = GeoService.validateGeoJSON(f);
    if (errors.length > 0) {
      throw new Error("GeoJSON issues: " + errors.map((e) => e.message).join(" | "));
    }
    return [f];
  }

  /**
   * Normalize a Geometry into a list of Features.
   * If it's a GeometryCollection, flatten it.
   * Invalid geometries are excluded.
   * @param geom Geometry
   * @returns Feature<any>[]
   */
  static normalizeGeometry(geom: Geometry): Feature<any>[] {
    if (!geom) return [];

    if (geom.type === "GeometryCollection") {
      const geomColl = geom as GeometryCollection;
      const validGeoms: any[] = [];
      geomColl.geometries.forEach((g) => {
        try {
          validGeoms.push(...this.normalizeGeometry(g));
        } catch (err) {
          console.warn("GeoJSON geometry excluded due to error:", err.message);
        }
      });
      return validGeoms;
    }

    const f = feature(geom);
    const errors = GeoService.validateGeoJSON(f);
    if (errors.length > 0) {
      throw new Error("GeoJSON issues: " + errors.map((e) => e.message).join(" | "));
    }
    return [f];
  }

  /**
   * Calculates the centroid of a WKT polygon using wellknown and turf/centroid.
   * Returns the centroid as [number, number] (longitude, latitude) or null if invalid.
   * Logs errors to console if WKT is invalid or centroid cannot be calculated.
   * @param wkt - WKT string representing a polygon or geometry
   * @returns {[number, number] | null} Centroid coordinates or null if invalid
   */
  static wktPolygonCentroid(wkt: string): [number, number] | null {
    try {
      // Convert WKT to GeoJSON geometry
      const geoJson = parse(wkt);
      if (!geoJson || !geoJson.type || !geoJson.coordinates) {
        console.error("Invalid WKT geometry for centroid:", wkt);
        return null;
      }
      const center = centroid(geoJson);
      if (!center || !center.geometry || !center.geometry.coordinates) {
        console.error("Could not calculate centroid for WKT:", wkt);
        return null;
      }
      const coords = center.geometry.coordinates;
      if (
        !Array.isArray(coords) ||
        coords.length !== 2 ||
        typeof coords[0] !== "number" ||
        typeof coords[1] !== "number"
      ) {
        console.error("Centroid coordinates are not valid numbers:", coords);
        return null;
      }
      // Return as [number, number] (longitude, latitude)
      return [coords[0], coords[1]];
    } catch (error) {
      console.error("Error in wktPolygonCentroid:", error, wkt);
      return null;
    }
  }

  static bboxFromCoordinates(coordinates: [number, number][]): any {
    const centroidPoints = coordinates.map((r) => point(r));
    if (centroidPoints.length > 0) {
      const fc = featureCollection(centroidPoints);
      return bbox(fc); // [minX, minY, maxX, maxY]
    }
    return null;
  }
}
